import '@styles/index.css';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '@store/hooks';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    loginSuccess,
    setLoginEmail,
    setLoggedInUserStatus,
    setLoggedInUserRole,
    setHospitalBlocked,
} from '@store/reducers/auth/loginSlice';
import QRCode from 'react-qr-code';
import {
    confirmSignIn,
    verifyTOTPSetup,
    updateMFAPreference,
} from 'aws-amplify/auth';
import AuthLayout from '@components/Layout/auth/AuthLayout';
import { HOME_PATH, LOGIN_PATH } from '@constants/routes';
import { TOTP_SRC } from '@constants/images';
import ErrorMessage from '@components/Error/ErrorMessage';
import LoadingSpinner from '@components/Spinner';
import { usePermissions } from '../../../../contexts/PermissionsContext';
import { ADMIN, HOSPITAL } from '@constants/constants';
import { processUserDataAndValidate } from '@utils/login';

const TOTP = () => {
    const qrSecret = useAppSelector((state) => state.login.qrSecret);
    const mfaPreferred = useAppSelector((state) => state.login.mfaPreferred);
    const email = useAppSelector((state) => state.login.email);
    const role = useAppSelector((state) => state.login.loggedInUserRole);
    const pushNotificationToken = useAppSelector((state) => state.user.pushNotificationToken);
    const [code, setCode] = useState(Array(6).fill(''));
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const inputRefs = useRef<HTMLInputElement[]>([]);
    const { fetchPermissions, permissions, loading } = usePermissions();

    const handleBeforeUnload = useCallback((event: BeforeUnloadEvent) => {
        const message = 'Are you sure you want to leave?';
        event.preventDefault();
        event.returnValue = message;
        return message;
    }, []);

    useEffect(() => {
        // window.addEventListener('beforeunload', handleBeforeUnload);

        if (email == '') {
            navigate(LOGIN_PATH);
        }

        // return () => {
        //     window.removeEventListener('beforeunload', handleBeforeUnload);
        // };
    }, [email, navigate]);

    const handleBackClick = () => {
        navigate(-1);
    };

    const handleCodeChange = (
        index: number,
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const newValue = event.target.value;

        if (newValue.length <= 1) {
            // Ensure only one character is entered
            const newValues = [...code];

            newValues[index] = newValue;
            setCode(newValues);

            // Move to the next input field if available
            if (newValue && index < inputRefs.current.length - 1) {
                inputRefs.current[index + 1].focus();
            }
        }

        setError(null);
    };

    const handleKeyDown = (
        index: number,
        event: React.KeyboardEvent<HTMLInputElement>
    ) => {
        const key = event.key;
        if (
            (key >= '0' && key <= '9') ||
            key === 'Backspace' ||
            key === 'Delete' ||
            key === 'ArrowLeft' ||
            key === 'ArrowRight' ||
            (event.ctrlKey && key.toLowerCase() === 'v') ||
            (event.ctrlKey && key.toLowerCase() === 'a')
        ) {
            // Special handling for backspace
            if (key === 'Backspace') {
                // If the field is empty and it's not the first field, move focus to the previous field
                if (event.currentTarget.value === '' && index > 0) {
                    inputRefs.current[index - 1]?.focus();
                }
            }
            return;
        }

        // Prevent all other keys
        event.preventDefault();
    };

    const handlePaste = (
        index: number,
        event: React.ClipboardEvent<HTMLInputElement>
    ) => {
        const pastedData = event.clipboardData.getData('text').slice(0, 6); // Limit to 6 characters
        const newValues = [...code];

        for (
            let i = 0;
            i < pastedData.length && index + i < newValues.length;
            i++
        ) {
            newValues[index + i] = pastedData[i];
        }

        setCode(newValues);

        // Focus on the next empty input field
        const nextIndex =
            index + pastedData.length < newValues.length
                ? index + pastedData.length
                : newValues.length - 1;

        inputRefs.current[nextIndex].focus();
    };

    const handleTOTP = async (event: React.SyntheticEvent) => {
        event.preventDefault();
        setIsLoading(true);
        try {
            if (mfaPreferred == true) {
                await confirmSignIn({
                    challengeResponse: code.join(''),
                });
            } else {
                await verifyTOTPSetup({
                    code: code.join(''),
                });

                await updateMFAPreference({ totp: 'ENABLED' });
            }

            await fetchPermissions();

            const userRole = await processUserDataAndValidate(
                email,
                pushNotificationToken || '',
                dispatch,
                navigate,
                HOME_PATH,
                setLoggedInUserRole,
                setLoggedInUserStatus,
                setHospitalBlocked
            );

            dispatch(setLoginEmail(email));
            dispatch(setLoggedInUserRole(userRole));

            dispatch(loginSuccess());
            // navigate(HOME_PATH);
            window.location.href = HOME_PATH;
        } catch (error: any) {
            // console.log(error);
            let errorMessage = 'Internal Server Error';

            switch (error.name) {
                case 'CodeMismatchException':
                    errorMessage = 'Invalid code received.';
                    break;
                default:
                    errorMessage = error.message || 'An unknown error occurred';
                    break;
            }

            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSkip = async () => {
        await fetchPermissions();

        const userRole = await processUserDataAndValidate(
            email,
            pushNotificationToken || '',
            dispatch,
            navigate,
            HOME_PATH,
            setLoggedInUserRole,
            setLoggedInUserStatus,
            setHospitalBlocked
        );

        dispatch(setLoginEmail(email));
        dispatch(setLoggedInUserRole(userRole));

        dispatch(loginSuccess());
        // navigate(HOME_PATH);
        window.location.href = HOME_PATH;
    };

    return (
        <AuthLayout onBackClick={handleBackClick}>
            <div className="flex flex-col justify-center mx-auto text-center width-30">
                {qrSecret != '' ? (
                    <>
                        <h4 className="text-2xl font-semibold text-nero mb-2">
                            {t('auth.totp.title')}
                        </h4>

                        <div className="mt-1">
                            <p className="text-netural_black text-lg font-normal mb-0">
                                {t('auth.totp.sub_title.text')}
                            </p>
                            <p className="text-netural_black text-lg font-normal mb-0">
                                {t('auth.totp.sub_title.text_continued')}
                            </p>
                        </div>

                        <div id="Container" className="mx-auto mt-3">
                            <QRCode level="H" size={150} value={qrSecret} />
                        </div>
                    </>
                ) : (
                    <>
                        <div className="mx-auto mt-3">
                            <img
                                src={TOTP_SRC}
                                alt="TOTP"
                                className="object-contain"
                            />
                        </div>
                    </>
                )}

                {error && <ErrorMessage message={error} />}

                <form onSubmit={handleTOTP}>
                    <div className={`mb-3 ${qrSecret != '' ? 'mt-2' : 'mt-2'}`}>
                        <label
                            htmlFor="totpCode"
                            className="mb-1 text-lg font-bold text-primary"
                        >
                            {qrSecret != '' ? (
                                <>{t('auth.totp.code_label')}</>
                            ) : (
                                <>{t('auth.totp.otp_verify')}</>
                            )}
                        </label>
                        <div className="">
                            <p className="text-secondary text-base font-normal mb-0">
                                {t('auth.totp.discription.text')}
                            </p>
                            <p className="text-secondary text-base font-normal mb-0">
                                {t('auth.totp.discription.text_continued')}
                            </p>
                        </div>
                        <div className="auth-input-wrapper flex items-center justify-between numeral-mask-wrapper mx-auto mt-3 lg:w-full xl:w-[80%] 2xl:w-[70%]">
                            {code.map((value, index) => (
                                <input
                                    key={index}
                                    type="text"
                                    aria-label="totpCode"
                                    maxLength={1}
                                    value={code[index] || ''}
                                    onChange={(event) =>
                                        handleCodeChange(index, event)
                                    }
                                    onKeyDown={(event) =>
                                        handleKeyDown(index, event)
                                    }
                                    onPaste={(event) =>
                                        handlePaste(index, event)
                                    }
                                    ref={(input) => {
                                        if (input) {
                                            inputRefs.current[index] = input;
                                        }
                                    }}
                                    required
                                    className="form-control auth-input h-px-50 text-center bg-whisper numeral-mask mx-1 my-2 lx:w-12 lg:w-11 2xl:w-[60%]"
                                />
                            ))}
                        </div>
                    </div>
                    {isLoading ? (
                        <div className="btn !bg-primary text-white font-semibold text-base py-2.5 2xl:w-5/6 lg:w-full xl:w-full mb-3 mx-auto">
                            <div>
                                <LoadingSpinner />
                            </div>
                        </div>
                    ) : (
                        <button
                            className="btn !bg-primary text-white font-semibold text-base py-2.5 2xl:w-5/6 lg:w-full xl:w-full mb-3 mx-auto"
                            type="submit"
                        >
                            {t('auth.totp.submit')}
                        </button>
                    )}

                    {qrSecret != '' && role.toLowerCase() != HOSPITAL.toLowerCase() && role.toLowerCase() != ADMIN.toLowerCase() && (
                        <div className="text-center !text-primary mb-3">
                            <div
                                color="primary"
                                className="w-2/4 cursor-pointer hover:underline underline-offset-4 mx-auto"
                                onClick={handleSkip}
                            >
                                {t('auth.totp.skip')}
                            </div>
                        </div>
                    )}
                </form>
            </div>
        </AuthLayout>
    );
};

export default TOTP;
